import { useQueryClient } from "@tanstack/react-query";
import { useTypedAppFormContext } from "@/components/Forms";
import {
  useCreateDeliveryOptionMutation,
  useFindAllDeliveryOptionsQuery,
  useFindDeliveryOptionQuery,
  useSetDeliveryOptionPartnerAssignmentsMutation,
  useUpdateDeliveryOptionMutation,
} from "@/libs/generated/hooks";
import {
  type DeliveryOptionFormValues,
  defaultValues,
} from "@/schemas/DeliveryForm/delivery-option.schema";
import { generateCode } from "@/utils/delivery-options";

interface SaveOptionArgs {
  isEdit: boolean;
  optionId?: number;
  value: DeliveryOptionFormValues;
}

function buildAssignmentPayload(
  partnerAssignments: DeliveryOptionFormValues["partnerAssignments"]
) {
  return partnerAssignments
    .filter((a) => a.selected)
    .map((a) => ({
      organizationId: a.organizationId,
      enabled: a.enabled,
      displayName: a.displayName.trim() || undefined,
      clientPrice: a.clientPrice || undefined,
    }));
}

export function useSaveDeliveryOption() {
  const queryClient = useQueryClient();

  const createMutation = useCreateDeliveryOptionMutation();
  const updateMutation = useUpdateDeliveryOptionMutation();
  const assignmentsMutation = useSetDeliveryOptionPartnerAssignmentsMutation();

  const isPending =
    createMutation.isPending || updateMutation.isPending || assignmentsMutation.isPending;

  function invalidateDeliveryQueries(optionId?: number) {
    queryClient.invalidateQueries({
      queryKey: useFindAllDeliveryOptionsQuery.getKey(),
      refetchType: "all",
    });
    queryClient.invalidateQueries({
      queryKey: ["FindDeliveryOptionsForPartner"],
      refetchType: "all",
    });
    if (optionId != null) {
      queryClient.invalidateQueries({
        queryKey: useFindDeliveryOptionQuery.getKey({ id: optionId }),
        refetchType: "all",
      });
    }
  }

  async function createOption(value: DeliveryOptionFormValues) {
    const {
      createDeliveryOption: { id },
    } = await createMutation.mutateAsync({
      createInput: {
        code: generateCode(value.name),
        name: value.name,
        defaultClientPrice: value.defaultClientPrice,
        visibilityMode: "GLOBAL" as never,
        requireInput: value.requireInput,
        requiresFile: value.requiresFile,
        requiresMultipleFiles: value.requiresMultipleFiles,
        dynamicPricing: value.dynamicPricing,
        fileType: value.fileType,
        active: value.active,
      },
    });

    return id;
  }

  async function updateOption(optionId: number, value: DeliveryOptionFormValues) {
    await updateMutation.mutateAsync({
      updateInput: {
        id: optionId,
        name: value.name,
        defaultClientPrice: value.defaultClientPrice,
        requireInput: value.requireInput,
        requiresFile: value.requiresFile,
        requiresMultipleFiles: value.requiresMultipleFiles,
        dynamicPricing: value.dynamicPricing,
        fileType: value.fileType,
        active: value.active,
        visibilityMode: value.visibilityMode as never,
      },
    });
  }

  async function setAssignments(
    optionId: number,
    assignments: ReturnType<typeof buildAssignmentPayload>
  ) {
    await assignmentsMutation.mutateAsync({
      setInput: {
        optionId,
        assignments,
      },
    });
  }

  async function saveOption({ isEdit, optionId, value }: SaveOptionArgs) {
    const isPartnerList = value.visibilityMode === "PARTNER_LIST";
    const assignments = buildAssignmentPayload(value.partnerAssignments);

    if (isEdit) {
      if (optionId == null) {
        throw new Error("optionId is required when editing");
      }

      if (isPartnerList) {
        await setAssignments(optionId, assignments);
      }

      await updateOption(optionId, value);
      invalidateDeliveryQueries(optionId);
      return;
    }

    const newOptionId = await createOption(value);

    if (isPartnerList) {
      await setAssignments(newOptionId, assignments);

      await updateMutation.mutateAsync({
        updateInput: {
          id: newOptionId,
          visibilityMode: "PARTNER_LIST" as never,
        },
      });
    }

    invalidateDeliveryQueries();
  }

  return {
    saveOption,
    isPending,
  };
}

export function useDeliveryOptionForm() {
  return useTypedAppFormContext({ defaultValues });
}
